import React from 'react';
import { PiWrenchLight } from 'react-icons/pi';
import {
  type WebsiteReadResponse,
  type WebsiteTemplateRegistryTemplateSummary,
  WebsiteTemplateRegistryVisibility,
  type WebsiteUpsertRequest,
} from 'src/api/generated';
import LabelNumberInput from 'src/modules/shared/components/atoms/inputs/label-number-input';
import LabelTextInput from 'src/modules/shared/components/atoms/inputs/label-text-input';
import { Text } from 'src/modules/shared/components/atoms/text';
import { InlineFormSection } from 'src/modules/shared/components/molecules/inline-form-section/inline-form-section';
import { SectionHeading } from 'src/modules/shared/components/molecules/section-heading/section-heading';
import { useI18n } from 'src/modules/shared/hooks/provider-bounded/i18n';
import { WebsitePendingLogoNotice } from '../molecules/website-pending-logo-notice';
import { WebsiteTemplatePicker } from '../molecules/website-template-picker';

/** Props for the design step. */
interface WebsiteDesignStepProps {
  /** Designs the tenant may use. */
  templates: WebsiteTemplateRegistryTemplateSummary[];
  /** Design the draft uses, when it is still selectable. */
  selectedTemplate?: WebsiteTemplateRegistryTemplateSummary;
  /** Website draft currently in the editor. */
  form: WebsiteUpsertRequest;
  /** Current website, used to mark the design in use. */
  website?: WebsiteReadResponse | null;
  /** Whether design changes are blocked right now. */
  selectionDisabled: boolean;
  /** Uploaded logo that must be saved before the design can change. */
  pendingLogoFileId: string | null;
  /** Whether the user is Comos staff and may see technical options. */
  showTechnicalOptions: boolean;
  /** Called with the design the user tries. */
  onSelectTemplate: (template: WebsiteTemplateRegistryTemplateSummary) => void;
  /** Called when staff type a template key by hand. */
  onTemplateKeyChange: (templateKey: string) => void;
  /** Called when staff type a template version by hand. */
  onTemplateVersionChange: (templateVersion: number) => void;
}

/**
 * Design step: the design cards and, for Comos staff only, the template key,
 * version, renderer, and registry visibility.
 *
 * @param props - Designs, draft, and selection callbacks.
 * @returns The design step panel.
 */
export const WebsiteDesignStep: React.FC<WebsiteDesignStepProps> = ({
  templates,
  selectedTemplate,
  form,
  website,
  selectionDisabled,
  pendingLogoFileId,
  showTechnicalOptions,
  onSelectTemplate,
  onTemplateKeyChange,
  onTemplateVersionChange,
}) => {
  const { t } = useI18n();
  const visibilityLabel =
    selectedTemplate?.visibility === WebsiteTemplateRegistryVisibility.PRIVATE
      ? t('websites.settings.template.private')
      : t('websites.settings.template.public');

  return (
    <section className="space-y-4">
      <SectionHeading
        title={t('websites.settings.design.title')}
        description={t('websites.settings.design.description')}
      />
      <WebsitePendingLogoNotice
        pendingLogoFileId={pendingLogoFileId}
        text={t('websites.settings.logo.saveBeforeTemplateChange')}
      />
      <WebsiteTemplatePicker
        templates={templates}
        selectedTemplate={selectedTemplate}
        form={form}
        website={website}
        disabled={selectionDisabled}
        onSelect={onSelectTemplate}
      />
      {showTechnicalOptions ? (
        <InlineFormSection
          title={t('websites.settings.design.technical.title')}
          description={t(
            'websites.settings.template.technicalConfigDescription'
          )}
          icon={<PiWrenchLight className="h-5 w-5" aria-hidden />}
        >
          <div className="grid gap-4 md:grid-cols-2">
            <LabelTextInput
              label={t('websites.settings.template.keyLabel')}
              labelPosition="top"
              inputProps={{
                value: form.templateKey,
                disabled: selectionDisabled,
                onChange: (event) => onTemplateKeyChange(event.target.value),
              }}
            />
            <LabelNumberInput
              label={t('websites.settings.template.versionLabel')}
              value={form.templateVersion}
              onChange={onTemplateVersionChange}
              min={1}
              step={1}
              disabled={selectionDisabled}
            />
          </div>
          <div className="space-y-1">
            <Text size="tiny" color="secondary">
              {t('websites.settings.design.technical.renderer', {
                renderer:
                  selectedTemplate?.preview?.renderer ??
                  selectedTemplate?.key ??
                  form.templateKey,
              })}
            </Text>
            {selectedTemplate ? (
              <Text size="tiny" color="secondary">
                {t('websites.settings.design.technical.registry', {
                  visibility: visibilityLabel,
                  registryTemplateId: selectedTemplate.registryTemplateId,
                })}
              </Text>
            ) : null}
          </div>
        </InlineFormSection>
      ) : null}
    </section>
  );
};
